import { ShipmentAllocation } from "@/components/inventory/shipment-picker";

import { Shipment } from "@/types/inventory/shipment";

import { DoseComponentFormValues } from "./dose-form-schema";
import { DoseComponentInventoryEntry } from "./use-dose-component-inventory";

/** Internal save intent, never a picker mode exposed to the caller. */
export enum DoseLotSelectionAction {
  DRAW = "DRAW",
  RECORD = "RECORD",
  KEEP = "KEEP",
}

export function getDoseLotCapabilities(
  component: DoseComponentFormValues,
  inventory: DoseComponentInventoryEntry,
) {
  const preview = inventory.preview;
  const canRecord = preview?.allowsClinicalLotReferences === true;
  const canCorrect =
    !canRecord &&
    !!component.id &&
    preview?.ordinaryEditMovesInventory === false;
  const canDraw =
    !!preview &&
    !inventory.previewError &&
    !canRecord &&
    ((preview.drawsAtDose && preview.ordinaryEditMovesInventory !== false) ||
      (canCorrect && component.correctActualInventoryUse === true));
  // Fail closed for contradictory preview permissions.
  const contradictory = canRecord && preview?.drawsAtDose === true;
  const action =
    !inventory.previewError && !contradictory && canRecord
      ? DoseLotSelectionAction.RECORD
      : canDraw && !!preview.stocks.length
        ? DoseLotSelectionAction.DRAW
        : DoseLotSelectionAction.KEEP;
  return { action, canCorrect: canCorrect && !inventory.previewError };
}

export function getRecordedDoseLots(component: DoseComponentFormValues) {
  return component.inventoryBaseline?.medicationId === component.medicationId
    ? (component.inventoryBaseline?.recordedLots ?? [])
    : [];
}

export function getDoseReceiptIds(component: DoseComponentFormValues) {
  return (
    component.clinicalShipmentIds ??
    getRecordedDoseLots(component).flatMap((lot) =>
      lot.shipmentId ? [lot.shipmentId] : [],
    )
  );
}

export function getDoseDisplayedAllocations(
  component: DoseComponentFormValues,
  inventory: DoseComponentInventoryEntry,
): NonNullable<DoseComponentFormValues["lotAllocations"]> {
  // Ledger history is display/owned credit, not a new explicit correction.
  return (
    component.lotAllocations ??
    inventory.preview?.ownedLots.map((lot) => ({
      shipmentId: lot.shipmentId,
      stockId: lot.stockId,
      quantity: lot.outstanding,
    })) ??
    []
  );
}

export function getDosePickerShipments(
  component: DoseComponentFormValues,
  inventory: DoseComponentInventoryEntry,
): Shipment[] {
  if (
    getDoseLotCapabilities(component, inventory).action !==
    DoseLotSelectionAction.DRAW
  )
    return [];
  return inventory.shipments
    .filter(
      (lot) =>
        inventory.preview?.stocks.some(
          (stock) =>
            stock.stockId === lot.stockId &&
            stock.unit &&
            Number.isFinite(stock.perAdministration) &&
            stock.perAdministration > 0,
        ) &&
        (!component.correctActualInventoryUse ||
          inventory.preview?.ownedLots.some(
            (owned) => owned.shipmentId === lot.id,
          )),
    )
    .map((lot) => ({
      id: lot.id,
      stockId: lot.stockId,
      locationId: lot.locationId,
      supplierId: lot.supplierId ?? "",
      supplier: { id: lot.supplierId ?? "", name: lot.supplierName ?? "" },
      remainingQuantity: lot.remainingQuantity,
      quantity: lot.quantity,
      cost: lot.cost ?? 0,
      lotNumber: lot.lotNumber,
      expiryDate: lot.expiryDate,
      createdDate: lot.createdDate,
    }));
}

export function getDoseInitialAllocations(
  component: DoseComponentFormValues,
  inventory: DoseComponentInventoryEntry,
  shipments: Shipment[],
): ShipmentAllocation[] {
  const byId = new Map(shipments.map((shipment) => [shipment.id, shipment]));
  return getDoseDisplayedAllocations(component, inventory).flatMap(
    (allocation) => {
      const shipment = byId.get(allocation.shipmentId);
      return shipment ? [{ ...allocation, shipment }] : [];
    },
  );
}

export function doseAllocationsUnchanged(
  selected: ShipmentAllocation[],
  initial: ShipmentAllocation[],
) {
  return (
    selected.length === initial.length &&
    selected.every((allocation) =>
      initial.some(
        (original) =>
          original.shipmentId === allocation.shipmentId &&
          original.quantity === allocation.quantity &&
          !!original.allowExtraYield === !!allocation.allowExtraYield &&
          original.decisionKey === allocation.decisionKey,
      ),
    )
  );
}
